import { StyledCard } from '@/app/features/common/DetailStyles';
import { FlexColumnDiv, FlexItemCenterDiv } from '@/app/shared/common.StyledComponents';
import styled from '@emotion/styled';
import { rem } from '@triumph/design-system';

export const ICON_SIZE = rem(24); 
export const ICON_GAP = rem(8);
export const CONTENT_OFFSET = `calc(${ICON_SIZE} + ${ICON_GAP})`;

export const WidgetContainer = styled(StyledCard)`
  padding: ${rem(16)};
  border: ${rem(1)} solid ${({ theme }: any) => theme.colors.greyscale.gray15};
  border-radius: ${rem(4)};
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  width: 100%;
  box-sizing: border-box;
  font-family: ${({ theme }: any) => theme.typography.fontFamilies.primary};
  line-height: ${({ theme }: any) => theme.typography.lineHeights.body.medium};
`;

export const WidgetHeader = styled(FlexColumnDiv)`
  gap: ${rem(4)};
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

export const HeaderSubtitle = styled.div`
  padding-left: ${rem(36)};

  & p {
    margin: 0;
  }
`;

export const UtilizationSummary = styled(FlexColumnDiv)`
  gap: ${rem(8)};
  padding-left: ${rem(8)};;
`;

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  margin-top: ${rem(4)};
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};

  & p {
    margin: 0;
  }
`;

export const LegendDot = styled.span<{ color: string }>`
  display: flex;
  width: ${rem(10)};
  height: ${rem(10)};
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
  align-self: center;
`;

export const Divider = styled.hr`
  border: none;
  border-top: ${rem(1)} solid ${({ theme }: any) => theme.colors.greyscale.gray15};
  margin: 0;
`;

export const CarriersTable = styled(FlexColumnDiv)`
  gap: 0;
`;

export const TableHeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(4)} 0 ${rem(8)};

  & p {
    margin: 0;
    font-weight: 800;
    font-size: ${rem(12)};
  }

  & p:first-child {
    flex: 1;
  }

  & p:last-child {
    min-width: ${rem(40)};
    text-align: right;
    flex-shrink: 0;
  }
`;

export const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(8)} 0;
`;

export const CarrierNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex: 1;
  min-width: 0;

  & p {
    margin: 0;
  }
`;

export const UtilizationCell = styled(FlexItemCenterDiv)`
  min-width: ${rem(40)};
  justify-content: flex-end;
  text-align: right;
  flex-shrink: 0;

  & p {
    margin: 0;
    text-align: right;
    width: 100%;
  }
`;

export const EmptyState = styled(FlexItemCenterDiv)`
  padding: ${rem(16)} 0;
  justify-content: center;

  & p {
    margin: 0;
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;

  & label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }

  & > svg {
    position: absolute;
    bottom: ${rem(8)};
    left: ${rem(4)};
    pointer-events: none;
    z-index: 1;
  }

  & input {
    padding-left: ${rem(28)};
  }
`;

export const SkeletonBlock = styled.div<{ width?: string; height?: string }>`
  background: ${({ theme }: any) => theme.colors.greyscale.gray15};
  border-radius: ${rem(4)};
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || rem(16)};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

export const SkeletonContainer = styled(FlexColumnDiv)`
  gap: ${rem(6)};
`;

export const SkeletonHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${ICON_GAP};
`;

export const SkeletonSubtitle = styled.div`
  margin-left: ${CONTENT_OFFSET};
`;

export const SkeletonSummary = styled(FlexColumnDiv)`
  gap: ${rem(8)};
  padding-left: ${CONTENT_OFFSET};
`;

export const SkeletonLegendRow = styled.div`
  display: flex;
  gap: ${rem(16)};
`;

export const SkeletonTableSection = styled(FlexColumnDiv)`
  gap: 0;
  padding-left: ${CONTENT_OFFSET};
`;

export const SkeletonTableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(8)} 0;
`;